import { useContext, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { copyToClipboard } from "../../utils/clipboard";
import { QRCodeSVG } from "qrcode.react";
import { Check, CheckCircle2, Copy, Loader2, Repeat } from "lucide-react";
import { PaymentContext } from "../../contexts/PaymentContext";
import { API_BASE } from "../../shared/Constants";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { reportHandledError, reportApiError } from "../../utils/reportError";

interface RecurringAuthData {
  authorization_id: string;
  status: string;
  deep_link?: string;
  emv?: string;
  amount: number;
  frequency: string;
  purpose: string;
}

type SetupState = "input" | "loading" | "pending" | "approved" | "rejected" | "error";

interface PixRecurringSetupProps {
  purpose: "subscription";
}

export default function PixRecurringSetup({ purpose }: PixRecurringSetupProps) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { setPaymentToast, billingScope, activeOrganizationId } = useContext(PaymentContext);

  const [amount, setAmount] = useState<string>("29.90");
  const [cpf, setCpf] = useState("");
  const [name, setName] = useState("");
  const [authData, setAuthData] = useState<RecurringAuthData | null>(null);
  const [setupState, setSetupState] = useState<SetupState>("input");
  const [copied, setCopied] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const pollIntervalRef = useRef<NodeJS.Timer | null>(null);

  const buildPixUrl = (path: string, params: Record<string, string> = {}) => {
    const search = new URLSearchParams(params);
    if (billingScope === "organization" && activeOrganizationId) {
      search.set("organization_id", activeOrganizationId);
    }
    const query = search.toString();
    return `${API_BASE}/api/billing/pix${path}${query ? `?${query}` : ""}`;
  };

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9)
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const formatDisplayAmount = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "R$ 0,00";
    return `R$ ${num.toFixed(2).replace(".", ",")}`;
  };

  const handleCreateAuth = async () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount < 20) {
      setInputMessage("O valor mínimo é R$ 20,00");
      return;
    }
    const cpfDigits = cpf.replace(/\D/g, "");
    if (cpfDigits.length !== 11) {
      setInputMessage("CPF inválido");
      return;
    }
    if (!name.trim()) {
      setInputMessage("Informe seu nome completo");
      return;
    }
    setSetupState("loading");
    try {
      const params: Record<string, string> = {
        amount: numAmount.toString(),
        purpose: purpose,
        payer_cpf: cpfDigits,
        payer_name: name.trim(),
        frequency: "MENSAL",
      };
      const url = buildPixUrl("/recurring/create-auth", params);
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Auth ${accessToken}` },
      });
      if (!response.ok) {
        reportApiError(response, { feature: "pix.recurring.create", method: "POST" });
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || "Erro ao criar autorização");
      }
      const data: RecurringAuthData = await response.json();
      setAuthData(data);
      setSetupState("pending");
    } catch (error: any) {
      reportHandledError(error, { category: "api", feature: "pix.recurring.create" });
      setSetupState("error");
      setPaymentToast({
        type: "error",
        message: error.message || "Erro ao criar autorização Pix",
      });
    }
  };

  useEffect(() => {
    if (setupState !== "pending" || !authData) return;
    const pollStatus = async () => {
      try {
        const params: Record<string, string> = {};
        if (billingScope === "organization" && activeOrganizationId) {
          params.organization_id = activeOrganizationId;
        }
        const url = buildPixUrl(`/recurring/auth-status/${authData.authorization_id}`, params);
        const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!accessToken) return;
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Auth ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status === "APPROVED") setSetupState("approved");
          else if (data.status === "REJECTED") setSetupState("rejected");
        } else {
          reportApiError(response, { feature: "pix.recurring.status", method: "GET" });
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "pix.recurring.status", level: "warning" });
      }
    };
    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, 5000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupState, authData]);

  const handleCopy = async () => {
    if (authData?.emv) {
      const ok = await copyToClipboard(authData.emv);
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  if (setupState === "input" || setupState === "loading" || setupState === "error") {
    return (
      <div className="flex flex-col gap-5 p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-foreground">
            <Repeat className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
              Pix Automático — Assinatura
            </h3>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Configure o Pix Automático para pagar sua assinatura mensalmente sem precisar
              escanear QR code.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-semibold text-foreground">CPF do pagador</label>
            <input
              type="text"
              placeholder="000.000.000-00"
              value={cpf}
              onChange={(e) => setCpf(formatCpf(e.target.value))}
              disabled={setupState === "loading"}
              className="rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-semibold text-foreground">Nome completo</label>
            <input
              type="text"
              placeholder="Seu nome completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={setupState === "loading"}
              className="rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
          {inputMessage && (
            <p className="text-[12px] font-medium text-destructive">{inputMessage}</p>
          )}
        </div>

        <div className="space-y-1 rounded-md border border-border bg-muted/30 p-4">
          <p className="text-[12px] font-bold uppercase tracking-wide text-foreground">
            Como funciona?
          </p>
          <p className="text-[13px] text-muted-foreground">
            1. Você autoriza o Pix Automático no app do seu banco
          </p>
          <p className="text-[13px] text-muted-foreground">
            2. As cobranças são feitas automaticamente a cada mês
          </p>
          <p className="text-[13px] text-muted-foreground">3. Você pode cancelar a qualquer momento</p>
        </div>

        <Button
          onClick={handleCreateAuth}
          disabled={setupState === "loading" || !cpf || !name}
          className="w-full gap-2"
        >
          {setupState === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Criando autorização...
            </>
          ) : (
            "Criar autorização Pix Automático"
          )}
        </Button>
      </div>
    );
  }

  if (setupState === "approved") {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <CheckCircle2 className="h-16 w-16 text-success" />
        <h3 className="text-xl font-semibold tracking-tight text-success">
          Pix Automático ativado!
        </h3>
        <p className="text-sm text-muted-foreground">
          Sua assinatura de {formatDisplayAmount(authData?.amount?.toString() || "0")}/mês está
          ativa.
        </p>
      </div>
    );
  }

  if (setupState === "rejected") {
    return (
      <div className="flex flex-col items-center gap-4 p-6 text-center">
        <h3 className="text-lg font-semibold tracking-tight text-destructive">
          Autorização rejeitada
        </h3>
        <p className="text-sm text-muted-foreground">
          O Pix Automático foi rejeitado pelo seu banco. Tente novamente ou use outro método de
          pagamento.
        </p>
        <Button
          onClick={() => {
            setSetupState("input");
            setAuthData(null);
          }}
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-foreground">
          <Repeat className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
            Aprovar Pix Automático
          </h3>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Escaneie o QR Code ou abra o link no app do seu banco para autorizar o pagamento
            recorrente.
          </p>
        </div>
      </div>

      <div className="flex justify-center">
        <span className="rounded-full border border-success/30 bg-success/10 px-4 py-1 text-base font-semibold text-success">
          {formatDisplayAmount(authData?.amount?.toString() || "0")}/mês
        </span>
      </div>

      {authData?.emv && (
        <div className="flex flex-col items-center gap-3">
          <Card className="p-4">
            <QRCodeSVG value={authData.emv} size={200} level="M" includeMargin />
          </Card>
          <div className="flex w-full flex-col gap-1.5">
            <label className="text-[13px] font-semibold text-foreground">
              Código Pix Automático
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                value={authData.emv}
                onClick={handleCopy}
                className="flex-1 cursor-pointer overflow-hidden text-ellipsis rounded-md border border-border bg-muted px-3 py-2 font-mono text-[12px] text-foreground/80 outline-none hover:border-foreground/30"
              />
              <Button type="button" variant="outline" size="icon" onClick={handleCopy}>
                {copied ? (
                  <Check className="h-4 w-4 text-success" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            {copied && <span className="text-[12px] font-medium text-success">Copiado!</span>}
          </div>
        </div>
      )}

      {authData?.deep_link && (
        <a
          href={authData.deep_link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Abrir no app do banco
        </a>
      )}

      <div className="flex items-center justify-center gap-2 rounded-md border border-warning/30 bg-warning/10 p-3">
        <Loader2 className="h-4 w-4 animate-spin text-warning" />
        <span className="text-[13px] font-medium text-warning">
          Aguardando aprovação no app do banco...
        </span>
      </div>
    </div>
  );
}
